// src/theme.ts
import { createTheme } from '@mui/material/styles'
import { ZoneTheme } from './shared/types'

const WHITE = '#f5f5f3'
const BLACK = '#16161D'

const RED = '#dc004e'
const RED2 = '#c62828'

// color should match _palette.scss
const codeStyles = {
  fontSize: '10px',
  fontFamily: 'monospace',
  whiteSpace: 'pre-wrap', // optional (to wrap long lines)
  // color: '#D12B2B', //'#F70000',
  color: '#00F7F7', //'#20B2AA',
}

export const getTheme = (props: ZoneTheme) =>
  createTheme({
    palette: {
      mode: 'dark', // enables dark mode (black bg, white text by default)
      background: {
        default: BLACK, // page background
        paper: BLACK, // card and paper backgrounds
      },
      text: {
        primary: WHITE,
        secondary: '#b0b0b0',
        disabled: '#808080',
      },
      textOnLight: {
        primary: BLACK,
        secondary: '#444',
      },
      primary: {
        main: WHITE,
      },
      secondary: {
        main: RED, // Error
      },
    },
    typography: {
      fontFamily: `'IBM Plex Sans', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,

      /* Headings */
      h1: {
        fontFamily: `'Libre Baskerville', 'Times New Roman', serif`,
        fontWeight: 700,
        fontSize: 'clamp(2.6rem, 5vw, 3.6rem)',
        lineHeight: 1.05,
        letterSpacing: '-0.035em',
        textTransform: 'uppercase',
        borderBottom: '2px solid rgba(255,255,255,0.85)',
        paddingBottom: '0.4rem',
        margin: 0,
        textRendering: 'optimizeLegibility',
        fontFeatureSettings: "'kern', 'liga', 'clig'",
      },
      h2: {
        fontFamily: `'Libre Baskerville', 'Times New Roman', serif`,
        fontWeight: 700,
        fontSize: 'clamp(1.5rem, 3.5vw, 2.5rem)',
        lineHeight: 1.15,
        letterSpacing: '-0.02em',
        textTransform: 'uppercase',
        margin: 0,
        textRendering: 'optimizeLegibility',
        fontFeatureSettings: "'kern', 'liga', 'clig'",
      },
      h3: {
        fontFamily: `'Libre Baskerville', 'Times New Roman', serif`,
        fontWeight: 600,
        fontSize: '1.35rem',
        lineHeight: 1.25,
        letterSpacing: '-0.015em',
        position: 'relative',
        margin: 0,
        textRendering: 'optimizeLegibility',
        fontFeatureSettings: "'kern', 'liga', 'clig'",
      },
      h4: {
        fontFamily: `'Libre Baskerville', 'Times New Roman', serif`,
        fontWeight: 600,
        fontSize: '1.2rem',
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
        fontStyle: 'italic',
        margin: 0,
      },
      h5: {
        fontFamily: `'Libre Baskerville', 'Times New Roman', serif`,
        fontWeight: 600,
        fontSize: '0.9rem',
        lineHeight: 1.4,
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        margin: 0,
      },
      h6: {
        fontFamily: `'Libre Baskerville', 'Times New Roman', serif`,
        fontWeight: 500,
        fontSize: '0.8rem',
        lineHeight: 1.4,
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
        margin: 0,
      },

      /* Body / paragraphs */
      body1: {
        fontSize: '1rem',
        lineHeight: 1.65,
        margin: 0,
        // maxWidth: '65ch',
      },
      body2: {
        fontSize: '1rem',
        lineHeight: 1.6,
      },

      /* Captions / small text */
      caption: {
        fontSize: '0.85rem',
        lineHeight: 1.5,
        letterSpacing: '0.01em',
        opacity: 0.75,
      },
      overline: {
        fontSize: '0.8rem',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        opacity: 0.7,
      },

      /* Custom */
      block: {
        display: 'block',
        fontSize: '10rem',
        fontWeight: 800,
        textTransform: 'uppercase',
        transform: 'rotate(-45deg)',
        // transformOrigin: 'left top', // optional, tweak pivot
        margin: 0,
      },
    },
    components: {
      MuiButton: {
        defaultProps: {
          variant: 'contained',
          color: 'primary', // Sets default button color
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 0, // default border radius
          },
        },
      },
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            h1: 'h1',
            h2: 'h2',
            h3: 'h3',
            h4: 'h4',
            h5: 'h5',
            h6: 'h6',
            body1: 'p',
            body2: 'p',
            caption: 'small',
            overline: 'time', // for meta / timestamps
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: BLACK,
            color: WHITE,
            fontFamily: `'IBM Plex Sans', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
            img: {
              filter: props?.grayscaleImages ? 'grayscale(100%)' : 'none',
              transition: 'filter 0.3s ease',
            },
            video: {
              filter: props?.grayscaleImages ? 'grayscale(100%)' : 'none',
              transition: 'filter 0.3s ease',
            },
          },
          p: {
            fontSize: '1rem',
            lineHeight: 1.65,
            margin: '0 0 1rem',
            maxWidth: '65ch',
          },
          blockquote: {
            fontStyle: 'italic',
            fontSize: '1.05rem',
            lineHeight: 1.7,
            margin: '1.5rem 0',
            paddingLeft: '1rem',
            borderLeft: '2px solid rgba(0,0,0,0.3)',
          },
          ul: {
            margin: '1rem 0 1.25rem 1.25rem',
            padding: 0,
          },
          ol: {
            margin: '1rem 0 1.25rem 1.25rem',
            padding: 0,
          },
          li: {
            marginBottom: '0.4rem',
          },
          strong: {
            fontWeight: 600,
          },
          em: {
            fontStyle: 'italic',
          },
          button: {
            fontFamily: 'inherit',
            fontSize: '0.9rem',
            letterSpacing: '0.04em',
          },
          input: {
            fontFamily: 'inherit',
          },
          textarea: {
            fontFamily: 'inherit',
          },
          label: {
            fontFamily: 'inherit',
          },
          pre: {
            ...codeStyles,
          },
          code: {
            ...codeStyles,
          },
        },
      },
    },
  })
